import { useEffect, useMemo } from "react";

import { useHistory } from "react-router-dom";

import { useGetOwnerDetails, useIsCryptoSupported } from "~/v2/api/hooks";
import type { Account, Product } from "~/v2/api/rest/account";
import { FeatureFlags, useFeatureEnabled } from "~/v2/helpers/featureFlags";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import {
  type UseAgreementReturn,
  useAgreement,
} from "./CoinTransfersAgreement.controller";
import {
  type UseLegacyAddWhitelistAddressReturn,
  useLegacyAddWhitelistAddress,
} from "./legacyAddWhitelistAddress";

export interface CoinTransfersControllerReturn {
  agreement: UseAgreementReturn;
  legacyAddWhitelistAddress: UseLegacyAddWhitelistAddressReturn;
  account: Account | undefined;
  accountId: string;
  product: Product | undefined;
  isLoading: boolean;
  isFetchingEnabled: boolean;
  isTRCEnabled: boolean;
  onAddWhitelistAddressClicked: () => void;
}

export const useCoinTransfersController = (): CoinTransfersControllerReturn => {
  const history = useHistory();
  const isTRCEnabled = useFeatureEnabled(FeatureFlags.CRYPTO_TRC);

  const { product, currentAccount } = useAccountContext({ liveOnly: true });
  const accountId = currentAccount?.id ?? "";

  const { details } = useGetOwnerDetails();

  const isAccountLoading = !currentAccount || !details;

  const isCryptoActive =
    currentAccount?.crypto_status === "ACTIVE" &&
    !currentAccount?.admin_configurations?.disable_crypto;

  const { isCryptoSupported, isLoading: isCryptoSupportedLoading } =
    useIsCryptoSupported();

  const agreement = useAgreement({
    accountId,
    isAccountLoading,
    isCryptoAgreementSigned: !!details?.crypto_agreement_signed_at,
    isCryptoActive,
    isCryptoSupported,
  });

  const isFetchingEnabled = useMemo(
    () =>
      !isAccountLoading &&
      isCryptoActive &&
      isCryptoSupported &&
      !agreement.isSettingUpAccount,
    [
      isAccountLoading,
      isCryptoActive,
      isCryptoSupported,
      agreement.isSettingUpAccount,
    ],
  );

  const legacyAddWhitelistAddress = useLegacyAddWhitelistAddress({
    accountId,
    isFetchingEnabled,
  });

  // Check if user is allowed to view crypto transfers page
  useEffect(() => {
    if (isCryptoSupportedLoading) {
      return;
    }
    // Redirect to dashboard if user is not allowed
    if (!isCryptoSupported) {
      history.replace(Path.ROUTE_DASHBOARD);
    }
  }, [history, isCryptoSupported, isCryptoSupportedLoading]);

  const onAddWhitelistAddressClicked = () => {
    if (isTRCEnabled) {
      history.push(Path.ROUTE_CRYPTO_WHITELIST_ADDRESS);
      return;
    }

    legacyAddWhitelistAddress.onOpen();
  };

  const isLoading = useMemo(() => {
    return isAccountLoading || isCryptoSupportedLoading;
  }, [isAccountLoading, isCryptoSupportedLoading]);

  return {
    isLoading,
    agreement,
    legacyAddWhitelistAddress,
    account: currentAccount,
    accountId,
    product,
    isFetchingEnabled,
    isTRCEnabled,
    onAddWhitelistAddressClicked,
  };
};
